import { CashListAccountType } from '@pflegenavi/shared/api';
import type { FC, PropsWithChildren } from 'react';
import { useMemo, useState } from 'react';
import {
  useCashListConfigurationPhoenix,
  useCashListsPhoenix,
} from '../cash-management-phoenix';
import type { ICashListConfigurationContext } from './CashListConfigurationContext';
import { CashListConfigurationContext } from './CashListConfigurationContext';

interface CashListConfigurationProviderProps {
  useNursingHomeContext: () => {
    selectedNursingHome?: {
      id: string;
    };
  };
}

export const CashListConfigurationProvider: FC<
  PropsWithChildren<CashListConfigurationProviderProps>
> = ({ children, useNursingHomeContext }) => {
  const { selectedNursingHome } = useNursingHomeContext();

  const { data: cashLists } = useCashListsPhoenix(
    {
      nursing_home_id: selectedNursingHome?.id,
    },
    {
      enabled: !!selectedNursingHome?.id,
    }
  );
  const { data: cashListConfiguration } = useCashListConfigurationPhoenix(
    selectedNursingHome?.id ?? '',
    {
      enabled: !!selectedNursingHome?.id,
    }
  );

  const [selectedCashListId, setSelectedCashListId] = useState<
    string | undefined
  >(undefined);

  const value = useMemo<ICashListConfigurationContext>(() => {
    const operationalCashListId = cashLists?.data?.find(
      (cashList) => cashList.account_type === CashListAccountType.Operational
    )?.id;
    return {
      // Fall back to operationalCashListId if none was selected
      selectedCashListId: selectedCashListId ?? operationalCashListId,
      cashLists:
        cashLists?.data?.map((cashList) => ({
          id: cashList.id,
          name: cashList.name,
          type: cashList.account_type,
          storageType: cashList.storage_type,
          totalInCent: cashList.total_cash,
          bankAccountAmountInCent: cashList.bank_account_amount,
          coins: cashList.coins,
          currency: cashList.currency_type,
          segment: cashList.segment,
          manualAdjustment: cashList.manual_adjustment,
          manualTransferOutgoing: cashList.manual_transfer_outgoing,
          manualTransferIncoming: cashList.manual_transfer_incoming,
          manualDepositPayin: cashList.manual_deposit_payin,
          manualDepositOther: cashList.manual_deposit_other,
          manualWithdrawalOther: cashList.manual_withdrawal_other,
          manualReceiptLinking: cashList.manual_receipt_linking,
          allowManualNegativeBankAccountAmount:
            cashList.allow_manual_negative_bank_account_amount,
        })) ?? [],
      setSelectedCashListId,
      payoutTargetCashListId:
        cashListConfiguration?.data?.payout_target_cash_list_id,
      payinTargetCashListId:
        cashListConfiguration?.data?.payin_target_cash_list_id,
      serviceProviderPaymentTargetCashListId:
        cashListConfiguration?.data
          ?.service_provider_payment_target_cash_list_id,
      pliantReferenceCashListId:
        cashListConfiguration?.data?.pliant_reference_cash_list_id,
      nursingHomeCashCashListId:
        cashListConfiguration?.data?.nursing_home_cash_cash_list_id,
      pliantCashCashListId:
        cashListConfiguration?.data?.pliant_cash_cash_list_id,
    };
  }, [cashLists?.data, cashListConfiguration?.data, selectedCashListId]);

  return (
    <CashListConfigurationContext.Provider value={value}>
      {children}
    </CashListConfigurationContext.Provider>
  );
};
